import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from './ui/sheet';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const CartSheet = () => {
  const { cartItems, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, getCartTotal } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (getCartTotal() < 30) {
      toast.error(`Pedido mínimo é R$ 30,00. Faltam R$ ${(30 - getCartTotal()).toFixed(2)}`);
      return;
    }
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-lg" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle
            className="text-2xl font-bold text-[#291014]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="cart-title"
          >
            Seu Pedido
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-full py-6">
          {cartItems.length === 0 ? (
            <div className="flex-1 flex items-center justify-center" data-testid="empty-cart-message">
              <p className="text-[#5C4247]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Seu carrinho está vazio
              </p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-4" data-testid="cart-items-list">
                {cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl p-4 border border-[#F3E8E3]"
                    data-testid={`cart-item-${item.id}`}
                  >
                    <div className="flex gap-4">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                        data-testid={`cart-item-image-${item.id}`}
                      />
                      <div className="flex-1">
                        <h4
                          className="font-bold text-[#291014] mb-1"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                          data-testid={`cart-item-name-${item.id}`}
                        >
                          {item.name}
                        </h4>
                        <p
                          className="text-sm text-[#7F1D1D] font-semibold"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                          data-testid={`cart-item-price-${item.id}`}
                        >
                          R$ {item.price.toFixed(2)}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 rounded-full border-[#7F1D1D] text-[#7F1D1D]"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            data-testid={`decrease-quantity-${item.id}`}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span
                            className="w-8 text-center font-semibold"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            data-testid={`cart-item-quantity-${item.id}`}
                          >
                            {item.quantity}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 rounded-full border-[#7F1D1D] text-[#7F1D1D]"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            data-testid={`increase-quantity-${item.id}`}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeFromCart(item.id)}
                            data-testid={`remove-item-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <SheetFooter className="border-t border-[#F3E8E3] pt-4 mt-4">
                <div className="w-full space-y-4">
                  <div className="flex justify-between items-center">
                    <span
                      className="text-lg font-semibold text-[#291014]"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      Total:
                    </span>
                    <span
                      className="text-2xl font-bold text-[#7F1D1D]"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                      data-testid="cart-total"
                    >
                      R$ {getCartTotal().toFixed(2)}
                    </span>
                  </div>
                  <Button
                    onClick={handleCheckout}
                    className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold py-6 rounded-full text-lg"
                    data-testid="checkout-button"
                  >
                    Finalizar Pedido
                  </Button>
                </div>
              </SheetFooter>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;